import ServiceSDKModule from "@gtt/service-sdk";
const Protocol =
  ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.service.meta
    .playsource.Protocol;
const AccessStatus =
  ServiceSDKModule.tech.gtt.frontapp.servicesdk.interfaces.service.channel
    .AccessStatus;
import { getWebAppCacheData } from "@/utils/cache";
import { MegaDecryptor, requestPlain } from "@/utils/mega";
import { parseM3U8MasterPlaylist } from "@/utils/playerInfo";
import store from "@/store";

const swProxyPath = "serviceProxy";
const mp4ContentType = "video/mp4";
const m3u8ContentType = "application/vnd.apple.mpegurl";
const appContentType = "application/x-mpegURL";

// 避免重复注册 Service Worker message 监听
let swMessageListenerRegistered = false;

/**
 * 监听 service worker 消息
 * @param {Event} event 事件对象
 */
export function initWebRtcSdkListener() {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker is not supported in this environment.");
    return;
  }
  if (swMessageListenerRegistered) {
    // 已经注册过监听，避免重复注册
    return;
  }
  swMessageListenerRegistered = true;
  navigator.serviceWorker.addEventListener("message", async (event) => {
    if (!event.data) return;
    const sdkInstance = getWebAppCacheData("webRtcSdkInstance");
    const activeStream = getWebAppCacheData("activeStream");
    if (!sdkInstance) {
      console.error("WebRTC SDK not found.");
      return;
    }
    const msg = event.data;
    const path = msg.url;

    // Mp4播放模式 网盘都是走mp4segment-needed分支
    if (msg.action === "mp4segment-needed") {
      try {
        console.log(`[${new Date().toLocaleString()}] mp4segment-needed, start`);        
        // 将这里的信息解析成对象，并存储到缓存中
        const parsed = parseM3U8MasterPlaylist("", `${activeStream?.logicStreamId || activeStream?.id}.mp4`);
        store.commit("player/set_playerInfo", parsed);
        const totalSize = await sdkInstance.waitUntil(() => sdkInstance.getMp4Filesize(), 60000, 200);
        let start = 0;
        let end = totalSize - 1;
        let responseStatus = 200;
        console.log("totalSize:", totalSize);

        if (msg.rangeHeader) {
          const matches = /bytes=(\d*)-(\d*)/.exec(msg.rangeHeader);
          if (matches) {
            if (matches[1]) start = parseInt(matches[1], 10);
            if (matches[2]) end = parseInt(matches[2], 10);
            else end = totalSize - 1;
          }

          const MAX_CHUNK_SIZE = 4 * 1024 * 1024; // MP4 播放默认 4MB，不足则不返回，每段加载播放，可根据需求自定义修改
          if (end - start + 1 > MAX_CHUNK_SIZE) {
            end = start + MAX_CHUNK_SIZE - 1;
          }
          end = Math.min(end, totalSize - 1);

          if (start > totalSize - 1) {
            responseStatus = 416;
            sendToSW(
              "segment",
              null,
              msg.messageId,
              mp4ContentType,
              responseStatus,
              start,
              end,
              totalSize
            );
            return;
          } else {
            responseStatus = 206;
          }
        }
        // 网盘需要解密这个 data（MP4的分段数据）
        let data = null;

        // 判断是否是网盘云端流，如果是则需要解密这个 data（MP4的分段数据）
        if (activeStream?.cloudStreamInfo?.cloudResKey) {
          const fileKey = activeStream?.cloudStreamInfo?.cloudResKey;
          console.log("fileKey:", fileKey);
          const megaDecryptor = new MegaDecryptor(fileKey);
          data = await requestPlain(
            megaDecryptor,         
            async (startPos: number, endPos: number) => {
              return sdkInstance.waitUntil(
                () =>
                  sdkInstance.getSegmentBytesData(
                    fileKey,
                    startPos,
                    endPos
                  ),
                180000,
                200
              );
            },
            start,
            end,
            totalSize
          );
        } else {
          data = await sdkInstance.waitUntil(() => sdkInstance.getSegmentBytesData(null, start, end));
        }
        
        sendToSW("segment", data, msg.messageId, mp4ContentType, responseStatus, start, end, totalSize);
      } catch (error) {
        console.error("Error fetching mp4segment:", error);
        sendToSW("segment", null, msg.messageId, mp4ContentType, 500);
      }
    } else if (msg.action === "segment-needed") {
      // 根据获取到的索引，获取M3U8的切片
      const name = path.replace(`/${swProxyPath}/`, "");
      try {
        const data = await sdkInstance.waitUntil(() => {
          return sdkInstance.getSegmentData(
            activeStream?.logicStreamId || activeStream?.streamId || activeStream?.id,
            name
          );
        });
        sendToSW("segment", data, msg.messageId, mp4ContentType, 200);
      } catch (error) {
        console.error("Error fetching segment:", error);
        sendToSW("segment", null, msg.messageId, mp4ContentType, 500);
      }
    } else if (msg.action === "manifest-needed") {
      // 索引文件走这里，获取主索引和音视频索引 video.m3u8和audio.m3u8
      try {
        let data;
        let masterM3u8 = "";
        // 获取流的主索引传给webRTC
        if (activeStream?.protocol === Protocol.Companion.fromValue("RPD")) {
          masterM3u8 = `${activeStream?.logicStreamId || activeStream?.id}.m3u8`;
        } else if (
          activeStream?.liveAuthority ||
          activeStream?.status === AccessStatus.ACCESS_GRANTED
        ) {
          masterM3u8 = `${activeStream?.streamId || activeStream?.id}.m3u8`;
        } else {
          console.log("流的权限有问题，拒绝获取主索引");
        }
        if (path.endsWith(masterM3u8)) {
          data = await sdkInstance.waitUntil(() =>
            sdkInstance.getFirstFloorM3u8()
          );
          // 将这里的信息解析成对象，并存储到缓存中
          const parsed = parseM3U8MasterPlaylist(data, masterM3u8);
          store.commit("player/set_playerInfo", parsed);
        } else if (path.endsWith("video.m3u8") || path.includes("video_")) {
          data = await sdkInstance.waitUntil(() =>
            sdkInstance.getVideoSegmentsM3u8()
          );
        } else if (path.endsWith("audio.m3u8") || path.includes("audio_")) {
          data = await sdkInstance.waitUntil(() =>
            sdkInstance.getAudioSegmentsM3u8()
          );
        } else {
          console.warn("Unknown manifest type for path:", path);
          sendToSW("manifest", null, msg.messageId, m3u8ContentType, 404);
          return;
        }
        sendToSW("manifest", data, msg.messageId, m3u8ContentType, 200);
      } catch (error) {
        console.error("Error fetching manifest:", error);
        sendToSW("manifest", null, msg.messageId, m3u8ContentType, 500);
      }
    } else if (msg.action === "keyid-needed") {
      // 获取公司加密，license key获取
      const data = getCompanyLicense(msg.keyID);
      sendToSW("segment", data, msg.messageId, mp4ContentType, 200);
    }
  });
}

/**
 * 发送消息到 service worker
 * @param {string} type 消息类型
 * @param {any} payload 消息内容
 * @param {string} messageId 消息ID
 * @param {string} contentType 内容类型
 * @param {number} status 状态码
 * @param {number} start 开始位置
 * @param {number} end 结束位置
 * @param {number} totalSize 总大小
 */
function sendToSW(
  type?: any,
  payload?: any,
  messageId?: any,
  contentType?: any,
  status?: any,
  start?: any,
  end?: any,
  totalSize?: any
) {
  if (!navigator.serviceWorker.controller) {
    console.warn(
      "Cannot send message: Service Worker is not controlling the page."
    );
    return;
  }

  const messagePayload = {
    messageId,
    type,
    payload,
    contentType,
    status: status || 200,
  } as any;

  if (start !== undefined) messagePayload.start = start;
  if (end !== undefined) messagePayload.end = end;
  if (totalSize !== undefined) messagePayload.totalSize = totalSize;

  if (payload) {
    let transferableBuffer: ArrayBufferLike | null = null;
    // 这个方式避免拷贝 payload，减少内存占用let transferableBuffer: ArrayBuffer | null = null;
    
    if (payload instanceof ArrayBuffer) {
      // 如果是 ArrayBuffer，直接使用
      transferableBuffer = payload;
    } else if (
      payload instanceof Uint8Array ||
      payload instanceof Int8Array ||
      payload instanceof Uint16Array ||
      payload instanceof Int16Array ||
      payload instanceof Uint32Array ||
      payload instanceof Int32Array ||
      payload instanceof Float32Array ||
      payload instanceof Float64Array
    ) {
      // 如果是其他 Array，使用其 buffer
      transferableBuffer = payload.buffer;
    } else if (payload.buffer instanceof ArrayBuffer) {
      // 其他有 buffer 属性的对象（如 DataView）
      transferableBuffer = payload.buffer;
    }
    console.log("transferableBuffer:", transferableBuffer);
    if (transferableBuffer) {
      // 使用可传输对象，避免拷贝，减少内存占用
      navigator.serviceWorker.controller.postMessage(messagePayload, [transferableBuffer]);
    } else {
      // 非可传输对象，使用结构化克隆
      navigator.serviceWorker.controller.postMessage(messagePayload);
    }
  } else {
    navigator.serviceWorker.controller.postMessage(messagePayload);
  }
}

/**
 * 获取 company 加密的 license
 * @param {string} keyId
 * @returns {ArrayBuffer}
 */
function getCompanyLicense(keyId: any) {
  const keyStr = hexToStr(keyId);
  const keyBuffer = new TextEncoder().encode(keyStr);

  return keyBuffer.buffer;
}

/**
 * 将十六进制字符串转换为字符串
 * @param {string} hex
 * @returns {string}
 */
function hexToStr(hex: any) {
  let str = "";
  for (let i = 0; i < hex.length; i += 2) {
    str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
  }
  return str;
}
